import { useEffect, useCallback, useRef, memo, Fragment } from 'react';
import PropTypes from 'prop-types';
import isEqual from 'lodash/isEqual';
import { useSoyuzLocales } from '@spotahome/soyuz/client';
import { useEcommerceManagerFactory } from '@spotahome/ecommerce-tracking';

import EmptyFavoritesLogged from './EmptyFavoritesLogged';
import EmptyFavoritesNotLogged from './EmptyFavoritesNotLogged';
import FavoriteMessageLogged from './FavoriteMessageLogged';
import FavoriteMessageNotLogged from './FavoriteMessageNotLogged';
import FavoritesHomecard from './FavoritesHomecard';

import './Favorites.scss';

const renderFavoriteMessage = isLogged =>
  isLogged ? <FavoriteMessageLogged /> : <FavoriteMessageNotLogged />;

const renderEmptyContent = isLogged =>
  isLogged ? <EmptyFavoritesLogged /> : <EmptyFavoritesNotLogged />;

const EMPTY_ARRAY = [];

const Favorites = ({
  homecards = EMPTY_ARRAY,
  isLogged = false,
  onToggleFavorite
}) => {
  const { current } = useSoyuzLocales();
  const prevHomecards = useRef(homecards);
  const ecommerceManager = useEcommerceManagerFactory();

  useEffect(() => {
    ecommerceManager.trackFavoritesImpressions(homecards, current);
  }, []);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    try {
      if (!isEqual(prevHomecards.current, homecards)) {
        ecommerceManager.trackFavoritesImpressions(homecards, current);
        prevHomecards.current = homecards; // update homecards reference
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Failed to track impressions at Favorites', err);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [homecards]); // locales doesn't change, should be part of the Instance initialization eventually

  const handleHomecardClick = useCallback(
    (homecard, position) => {
      ecommerceManager.trackFavoriteClick(homecard, current, position);
    },
    [current]
  );

  return (
    <div className="favorites">
      {homecards.length ? (
        <Fragment>
          {renderFavoriteMessage(isLogged)}
          <div className="favorites__homecards-wrapper">
            {homecards.map((homecard, position) => (
              <FavoritesHomecard
                key={homecard.id}
                homecard={homecard}
                onToggleFavorite={onToggleFavorite}
                onClick={() => handleHomecardClick(homecard, position)}
              />
            ))}
          </div>
        </Fragment>
      ) : (
        renderEmptyContent(isLogged)
      )}
    </div>
  );
};

Favorites.propTypes = {
  homecards: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      currencyLabel: PropTypes.string,
      currencySymbol: PropTypes.string,
      currencyIsoCode: PropTypes.string
    })
  ),
  isLogged: PropTypes.bool,
  onToggleFavorite: PropTypes.func.isRequired
};

export default memo(Favorites);
